import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Clock, RefreshCw, Globe, Download, Calendar } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';

const timezones = [
  { name: 'UTC', offset: 0, label: 'UTC (Coordinated Universal Time)' },
  { name: 'EST', offset: -5, label: 'EST (Eastern Standard Time)' },
  { name: 'PST', offset: -8, label: 'PST (Pacific Standard Time)' },
  { name: 'GMT', offset: 0, label: 'GMT (Greenwich Mean Time)' },
  { name: 'CET', offset: 1, label: 'CET (Central European Time)' },
  { name: 'JST', offset: 9, label: 'JST (Japan Standard Time)' },
  { name: 'CST', offset: 8, label: 'CST (China Standard Time)' },
  { name: 'IST', offset: 5.5, label: 'IST (India Standard Time)' },
  { name: 'AEST', offset: 10, label: 'AEST (Australian Eastern Standard Time)' },
];

const dateFormats = [
  { name: 'ISO 8601', example: '2024-01-15T10:30:00.000Z' },
  { name: 'RFC 2822', example: 'Mon, 15 Jan 2024 10:30:00 +0000' },
  { name: 'US Format', example: '01/15/2024 10:30:00' },
  { name: 'EU Format', example: '15/01/2024 10:30:00' },
  { name: 'Long Format', example: 'Monday, January 15th 2024, 10:30:00 AM' },
  { name: 'Short Format', example: 'Jan 15, 2024 10:30' },
];

export const TimestampConverterTool: React.FC = () => {
  const { t } = useTranslation();
  const [timestamp, setTimestamp] = useState(Math.floor(Date.now() / 1000));
  const [humanDate, setHumanDate] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedTimezone, setSelectedTimezone] = useState('UTC');
  const [selectedFormat, setSelectedFormat] = useState('ISO 8601');

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    convertTimestampToHuman(timestamp);
  }, [timestamp]);

  const convertTimestampToHuman = (ts: number) => {
    try {
      const date = new Date(ts * 1000);
      if (isNaN(date.getTime())) {
        setHumanDate(t('tools.timestampConverter.invalidTimestamp'));
        return;
      }
      setHumanDate(date.toISOString());
    } catch (err) {
      setHumanDate(t('tools.timestampConverter.invalidTimestamp'));
    }
  };

  const convertHumanToTimestamp = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) {
        return;
      }
      setTimestamp(Math.floor(date.getTime() / 1000));
    } catch (err) {
      // Invalid date string
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text.toString());
      toast.success(t('common.copied'));
    } catch (err) {
      toast.error(t('common.failedToCopy'));
    }
  };

  const setCurrentTimestamp = () => {
    setTimestamp(Math.floor(Date.now() / 1000));
  };

  const formatDate = (date: Date, format: string) => {
    const options: Intl.DateTimeFormatOptions = {};
    switch (format) {
      case 'iso':
        return date.toISOString();
      case 'utc':
        return date.toUTCString();
      case 'local':
        return date.toLocaleString();
      case 'date':
        return date.toDateString();
      case 'time':
        return date.toTimeString();
      default:
        return date.toString();
    }
  };

  const presetTimestamps = [
    { name: t('tools.timestampConverter.unixEpoch'), value: 0 },
    { name: t('tools.timestampConverter.y2k'), value: 946684800 },
    { name: t('tools.timestampConverter.iphoneLaunch'), value: 1183248000 },
    { name: t('tools.timestampConverter.bitcoinGenesis'), value: 1231006505 },
    { name: t('tools.timestampConverter.covidDeclaration'), value: 1583971200 },
  ];

  const formatDateWithTimezone = (date: Date, timezone: string, format: string) => {
    const tz = timezones.find(t => t.name === timezone);
    if (!tz) return date.toISOString();

    const offsetMs = tz.offset * 60 * 60 * 1000;
    const adjustedDate = new Date(date.getTime() + offsetMs);

    switch (format) {
      case 'ISO 8601':
        return adjustedDate.toISOString();
      case 'RFC 2822':
        return adjustedDate.toUTCString();
      case 'US Format':
        return adjustedDate.toLocaleDateString('en-US') + ' ' + adjustedDate.toLocaleTimeString('en-US');
      case 'EU Format':
        return adjustedDate.toLocaleDateString('en-GB') + ' ' + adjustedDate.toLocaleTimeString('en-GB');
      case 'Long Format':
        return adjustedDate.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      case 'Short Format':
        return adjustedDate.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      default:
        return adjustedDate.toString();
    }
  };

  const exportTimestampData = () => {
    const date = new Date(timestamp * 1000);
    const data = {
      timestamp: timestamp,
      formats: dateFormats.map(format => ({
        name: format.name,
        value: formatDateWithTimezone(date, selectedTimezone, format.name)
      })),
      timezones: timezones.map(tz => ({
        name: tz.name,
        label: tz.label,
        value: formatDateWithTimezone(date, tz.name, selectedFormat)
      })),
      exported: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `timestamp-${timestamp}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Current Time Display */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Clock className="w-5 h-5" />
            <span>{t('tools.timestampConverter.currentTime')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Current Timestamp
              </label>
              <div className="flex items-center space-x-2">
                <div className="font-mono text-lg bg-gray-50 dark:bg-gray-800 p-3 rounded border flex-1">
                  {Math.floor(currentTime.getTime() / 1000)}
                </div>
                <Button
                  onClick={() => copyToClipboard(Math.floor(currentTime.getTime() / 1000).toString())}
                  size="sm"
                  variant="outline"
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Current Date & Time
              </label>
              <div className="flex items-center space-x-2">
                <div className="font-mono text-sm bg-gray-50 dark:bg-gray-800 p-3 rounded border flex-1">
                  {currentTime.toISOString()}
                </div>
                <Button
                  onClick={() => copyToClipboard(currentTime.toISOString())}
                  size="sm"
                  variant="outline"
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Format and Timezone Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Globe className="w-5 h-5" />
              <span>Timezone & Format</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Timezone
                </label>
                <select
                  value={selectedTimezone}
                  onChange={(e) => setSelectedTimezone(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  {timezones.map((tz) => (
                    <option key={tz.name} value={tz.name}>
                      {tz.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Date Format
                </label>
                <select
                  value={selectedFormat}
                  onChange={(e) => setSelectedFormat(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  {dateFormats.map((format) => (
                    <option key={format.name} value={format.name}>
                      {format.name}
                    </option>
                  ))}
                </select>
              </div>
              <Button
                onClick={exportTimestampData}
                variant="outline"
                className="w-full"
              >
                <Download className="w-4 h-4 mr-2" />
                Export Data
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Format Examples</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              {dateFormats.map((format) => (
                <div key={format.name} className="flex justify-between">
                  <span className="font-medium">{format.name}:</span>
                  <span className="font-mono text-gray-600 dark:text-gray-400">
                    {format.example}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Converter */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('tools.timestampConverter.timestamp')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Input
                  type="number"
                  value={timestamp}
                  onChange={(e) => setTimestamp(parseInt(e.target.value) || 0)}
                  className="font-mono"
                  placeholder="1234567890"
                />
                <Button
                  onClick={setCurrentTimestamp}
                  size="sm"
                  variant="outline"
                  title={t('tools.timestampConverter.useCurrentTime')}
                >
                  <RefreshCw className="w-3 h-3" />
                </Button>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('tools.timestampConverter.presetTimestamps')}
                </label>
                <div className="space-y-1">
                  {presetTimestamps.map((preset) => (
                    <Button
                      key={preset.name}
                      onClick={() => setTimestamp(preset.value)}
                      variant="outline"
                      size="sm"
                      className="w-full justify-between text-left"
                    >
                      <span>{preset.name}</span>
                      <span className="font-mono text-xs">{preset.value}</span>
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('tools.timestampConverter.humanDate')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Input
                type="datetime-local"
                value={humanDate.slice(0, 19)}
                onChange={(e) => {
                  setHumanDate(e.target.value + 'Z');
                  convertHumanToTimestamp(e.target.value);
                }}
              />
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('tools.timestampConverter.orEnterDateString')}
                </label>
                <Input
                  value={humanDate}
                  onChange={(e) => {
                    setHumanDate(e.target.value);
                    convertHumanToTimestamp(e.target.value);
                  }}
                  placeholder="2023-12-25T12:00:00Z"
                  className="font-mono"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Format Examples */}
      {timestamp && !isNaN(new Date(timestamp * 1000).getTime()) && (
        <Card>
          <CardHeader>
            <CardTitle>Date Formats</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { name: t('tools.timestampConverter.iso8601'), format: 'iso' },
                { name: t('tools.timestampConverter.utcString'), format: 'utc' },
                { name: t('tools.timestampConverter.localString'), format: 'local' },
                { name: t('tools.timestampConverter.dateOnly'), format: 'date' },
                { name: t('tools.timestampConverter.timeOnly'), format: 'time' },
              ].map(({ name, format }) => {
                const date = new Date(timestamp * 1000);
                const formatted = formatDate(date, format);
                return (
                  <div key={format} className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 w-24">
                      {name}:
                    </span>
                    <div className="flex items-center space-x-2 flex-1">
                      <div className="font-mono text-sm bg-gray-50 dark:bg-gray-800 p-2 rounded border flex-1">
                        {formatted}
                      </div>
                      <Button
                        onClick={() => copyToClipboard(formatted)}
                        size="sm"
                        variant="ghost"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Timezone Information */}
      {timestamp && !isNaN(new Date(timestamp * 1000).getTime()) && (
        <Card>
          <CardHeader>
            <CardTitle>{t('tools.timestampConverter.timezoneInformation')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
              {[
                { name: 'UTC', offset: 0 },
                { name: 'EST', offset: -5 },
                { name: 'PST', offset: -8 },
                { name: 'GMT+1', offset: 1 },
                { name: 'JST', offset: 9 },
                { name: 'AEST', offset: 10 },
              ].map(({ name, offset }) => {
                const date = new Date(timestamp * 1000);
                const offsetDate = new Date(date.getTime() + offset * 60 * 60 * 1000);
                return (
                  <div key={name} className="border border-gray-200 dark:border-gray-700 rounded p-3">
                    <div className="font-medium text-gray-900 dark:text-gray-100 mb-1">
                      {name}
                    </div>
                    <div className="font-mono text-xs text-gray-600 dark:text-gray-400">
                      {offsetDate.toISOString().replace('T', ' ').slice(0, 19)}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Information */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tools.timestampConverter.aboutUnixTimestamps')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 text-sm text-gray-600 dark:text-gray-400">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">{t('tools.timestampConverter.whatIsUnixTimestamp')}</h4>
              <p>
                {t('tools.timestampConverter.unixTimestampDescription')}
              </p>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">{t('tools.timestampConverter.commonUseCases')}</h4>
              <ul className="list-disc list-inside space-y-1">
                <li>{t('tools.timestampConverter.databaseStorage')}</li>
                <li>{t('tools.timestampConverter.apiTimestamps')}</li>
                <li>{t('tools.timestampConverter.logFiles')}</li>
                <li>{t('tools.timestampConverter.sessionExpiration')}</li>
                <li>{t('tools.timestampConverter.fileModification')}</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">{t('tools.timestampConverter.importantNotes')}</h4>
              <ul className="list-disc list-inside space-y-1">
                <li>{t('tools.timestampConverter.alwaysUtc')}</li>
                <li>{t('tools.timestampConverter.maxTimestamp')}</li>
                <li>{t('tools.timestampConverter.millisecondsNote')}</li>
                <li>{t('tools.timestampConverter.leapSecondsNote')}</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
